import React, { useEffect, useMemo, useState } from 'react';
import { io } from 'socket.io-client';
import './App.css';

const serverUrl =
  process.env.REACT_APP_SERVER_URL ||
  window.location.origin;

const socket = io(serverUrl, {
  transports: ['websocket', 'polling'],
});

const getTelegramUser = () => window.Telegram?.WebApp?.initDataUnsafe?.user || null;
const getTelegramInitData = () => window.Telegram?.WebApp?.initData || '';

const getTelegramName = () => {
  const user = getTelegramUser();

  if (!user) {
    return '';
  }

  return user.username || [user.first_name, user.last_name].filter(Boolean).join(' ');
};

const getLocalUserId = () => {
  const key = 'brehlo-local-user-id';
  const existing = window.localStorage.getItem(key);

  if (existing) {
    return existing;
  }

  const next = window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`;
  window.localStorage.setItem(key, next);
  return next;
};

const getInitialNickname = () =>
  getTelegramName() || window.localStorage.getItem('brehlo-display-name') || '';

const percent = (part, total) => {
  if (!total) {
    return '0%';
  }

  return `${Math.round((part / total) * 100)}%`;
};

const buildAuthPayload = (nickname) => ({
  nickname,
  initData: getTelegramInitData(),
  telegramUser: getTelegramUser(),
  localUserId: getLocalUserId(),
});

function StatTile({ label, value }) {
  return (
    <div className="stat-tile">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function App() {
  const [nickname, setNickname] = useState(getInitialNickname);
  const [roomCode, setRoomCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [room, setRoom] = useState(null);
  const [profile, setProfile] = useState(null);
  const [roleCard, setRoleCard] = useState(null);
  const [result, setResult] = useState(null);
  const [selectedVote, setSelectedVote] = useState('');
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [notice, setNotice] = useState('');
  const [connected, setConnected] = useState(socket.connected);

  useEffect(() => {
    window.Telegram?.WebApp?.ready();
    window.Telegram?.WebApp?.expand();

    const onConnect = () => {
      setConnected(true);
      socket.emit('identify', buildAuthPayload(nickname));
    };
    const onDisconnect = () => setConnected(false);
    const onProfileUpdated = (nextProfile) => {
      setProfile(nextProfile);
      setNickname((current) => current || nextProfile.displayName);
    };
    const onRoomJoined = (nextRoom) => {
      setRoom(nextRoom);
      setRoomCode(nextRoom.id);
      setNotice('');
    };
    const onRoomUpdated = (nextRoom) => {
      setRoom(nextRoom);
      setRoomCode(nextRoom.id);
    };
    const onRoleAssigned = (card) => {
      setRoleCard(card);
      setResult(null);
      setSelectedVote('');
      setNotice('');
    };
    const onVotingFinished = (roundResult) => {
      setResult(roundResult);
      setSelectedVote('');
    };
    const onRoundCancelled = (text) => {
      setRoleCard(null);
      setResult(null);
      setSelectedVote('');
      setNotice(text);
    };
    const onGameError = (text) => setNotice(text);
    const onNewMessage = (newMessage) => {
      setMessages((current) => [...current.slice(-20), newMessage]);
    };

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('profileUpdated', onProfileUpdated);
    socket.on('roomJoined', onRoomJoined);
    socket.on('roomUpdated', onRoomUpdated);
    socket.on('roleAssigned', onRoleAssigned);
    socket.on('votingFinished', onVotingFinished);
    socket.on('roundCancelled', onRoundCancelled);
    socket.on('gameError', onGameError);
    socket.on('newMessage', onNewMessage);

    if (socket.connected) {
      socket.emit('identify', buildAuthPayload(nickname));
    }

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('profileUpdated', onProfileUpdated);
      socket.off('roomJoined', onRoomJoined);
      socket.off('roomUpdated', onRoomUpdated);
      socket.off('roleAssigned', onRoleAssigned);
      socket.off('votingFinished', onVotingFinished);
      socket.off('roundCancelled', onRoundCancelled);
      socket.off('gameError', onGameError);
      socket.off('newMessage', onNewMessage);
    };
  }, [nickname]);

  useEffect(() => {
    if (nickname.trim()) {
      window.localStorage.setItem('brehlo-display-name', nickname.trim());
    }
  }, [nickname]);

  const currentPlayer = useMemo(
    () => room?.players.find((player) => player.id === socket.id),
    [room]
  );
  const activeProfile = currentPlayer || profile;
  const stats = activeProfile?.stats;
  const isHost = room?.hostId === socket.id;
  const canStart = room && room.players.length >= room.minPlayers && room.phase === 'lobby';
  const hasVoted = Boolean(currentPlayer?.hasVoted);
  const otherPlayers = room?.players.filter((player) => player.id !== socket.id) || [];
  const leaderboard = result?.leaderboard || room?.leaderboard || [];
  const usesTelegram = Boolean(getTelegramUser());

  const createRoom = () => {
    socket.emit('createRoom', buildAuthPayload(nickname));
  };

  const joinRoom = () => {
    socket.emit('joinRoom', { ...buildAuthPayload(nickname), roomId: joinCode });
  };

  const startGame = () => {
    socket.emit('startGame', { roomId: room.id });
  };

  const vote = (targetId) => {
    setSelectedVote(targetId);
    socket.emit('vote', { roomId: room.id, targetId });
  };

  const nextRound = () => {
    socket.emit('nextRound', { roomId: room.id });
  };

  const backToLobby = () => {
    socket.emit('backToLobby', { roomId: room.id });
    setRoleCard(null);
    setResult(null);
  };

  const sendMessage = (event) => {
    event.preventDefault();
    socket.emit('sendMessage', { roomId: room.id, message });
    setMessage('');
  };

  return (
    <main className="app-shell">
      <section className="topbar">
        <div>
          <p className="eyebrow">Telegram party game</p>
          <h1>Брехло</h1>
        </div>
        <span className={`connection ${connected ? 'online' : 'offline'}`}>
          {connected ? 'онлайн' : 'нет связи'}
        </span>
      </section>

      {notice && <div className="notice">{notice}</div>}

      {!room && (
        <section className="entry-layout">
          <div className="hero-panel">
            <div className="mark">Б</div>
            <h2>Один знает меньше всех. Главное — не выдать себя.</h2>
            <p>Создайте комнату, назовите код друзьям за столом и запускайте раунд.</p>
          </div>

          <div className="join-panel">
            <div className="profile-strip">
              <span className="avatar big">{(nickname || 'И').slice(0, 1).toUpperCase()}</span>
              <div>
                <strong>{profile?.displayName || nickname || 'Игрок'}</strong>
                <small>{profile?.title || 'Новичок за столом'} · {profile?.rating || 0} очков</small>
              </div>
            </div>

            <label>
              Имя за столом
              <input
                value={nickname}
                maxLength={24}
                disabled={usesTelegram}
                onChange={(event) => setNickname(event.target.value)}
                placeholder="Например, Женя"
              />
            </label>

            <p className="hint">
              {usesTelegram
                ? 'Имя и рейтинг привязаны к вашему Telegram ID.'
                : 'В браузере используется локальный профиль. В Telegram рейтинг будет привязан к Telegram ID.'}
            </p>

            <button className="primary-action" onClick={createRoom} disabled={!nickname.trim()}>
              Создать комнату
            </button>

            <div className="divider">или войти по коду</div>

            <label>
              Код комнаты
              <input
                value={joinCode}
                inputMode="numeric"
                maxLength={4}
                onChange={(event) => setJoinCode(event.target.value.replace(/\D/g, '').slice(0, 4))}
                placeholder="1234"
              />
            </label>

            <button onClick={joinRoom} disabled={!nickname.trim() || joinCode.length !== 4}>
              Присоединиться
            </button>
          </div>
        </section>
      )}

      {room && (
        <section className="room-layout">
          <div className="room-header">
            <div>
              <p className="eyebrow">Комната</p>
              <button className="room-code" onClick={() => navigator.clipboard?.writeText(roomCode)}>
                {roomCode}
              </button>
            </div>
            <div className="round-pill">
              {room.phase === 'lobby' ? 'Лобби' : `Раунд ${room.round}`}
            </div>
          </div>

          {activeProfile && (
            <div className="profile-card">
              <div>
                <p className="eyebrow">Ваш профиль</p>
                <h2>{activeProfile.nickname || activeProfile.displayName}</h2>
                <p>{activeProfile.title} · {activeProfile.rating} очков</p>
              </div>
              {stats && (
                <div className="stats-grid">
                  <StatTile label="игр" value={stats.gamesPlayed} />
                  <StatTile label="побед" value={stats.wins} />
                  <StatTile label="обман" value={percent(stats.successfulBluffs, stats.playedAsBrehlo)} />
                  <StatTile label="точность" value={percent(stats.correctVotes, stats.votesCast)} />
                </div>
              )}
            </div>
          )}

          <div className="players-grid">
            {room.players.map((player) => (
              <div className="player-tile" key={player.id}>
                <span className="avatar">{player.nickname.slice(0, 1).toUpperCase()}</span>
                <span>{player.nickname}</span>
                <small>{player.title} · {player.rating}</small>
                {player.isHost && <small>ведущий</small>}
                {room.phase === 'playing' && player.hasVoted && <small>голос есть</small>}
              </div>
            ))}
          </div>

          {leaderboard.length > 0 && (
            <div className="leaderboard-panel">
              <h3>Рейтинг за столом</h3>
              {leaderboard.map((player, index) => (
                <div className="leader-row" key={player.id}>
                  <span>{index + 1}</span>
                  <strong>{player.nickname}</strong>
                  <small>{player.title}</small>
                  <b>{player.rating}</b>
                </div>
              ))}
            </div>
          )}

          {room.phase === 'lobby' && (
            <div className="action-panel">
              <p>
                Игроков: {room.players.length}/{room.maxPlayers}. Минимум для старта: {room.minPlayers}.
              </p>
              {isHost ? (
                <button className="primary-action" onClick={startGame} disabled={!canStart}>
                  Начать раунд
                </button>
              ) : (
                <p className="muted">Ждем, пока ведущий начнет игру.</p>
              )}
            </div>
          )}

          {room.phase === 'playing' && roleCard && (
            <div className={`role-card ${roleCard.role === 'brehlo' ? 'liar' : 'player'}`}>
              <p className="eyebrow">Ваша карточка</p>
              {roleCard.role === 'brehlo' ? (
                <>
                  <h2>Вы Брехло</h2>
                  <p>Слушайте намеки и говорите так, будто знаете слово.</p>
                </>
              ) : (
                <>
                  <h2>{roleCard.word}</h2>
                  <p>Давайте намеки аккуратно, без прямого ответа.</p>
                </>
              )}
            </div>
          )}

          {room.phase === 'playing' && (
            <div className="vote-panel">
              <h3>Кого подозреваете?</h3>
              <div className="vote-list">
                {otherPlayers.map((player) => (
                  <button
                    key={player.id}
                    className={selectedVote === player.id ? 'selected' : ''}
                    onClick={() => vote(player.id)}
                    disabled={hasVoted}
                  >
                    {player.nickname}
                  </button>
                ))}
              </div>
              {hasVoted && <p className="muted">Голос принят. Ждем остальных.</p>}
            </div>
          )}

          {room.phase === 'results' && result && (
            <div className="result-panel">
              <p className="eyebrow">Итог голосования</p>
              <h2>{result.caught ? 'Брехло раскрыт' : 'Брехло выкрутился'}</h2>
              <p>
                Брехло: <strong>{result.brehloNickname}</strong>. Слово: <strong>{result.secret}</strong>.
              </p>
              <div className="score-list">
                {result.counts
                  .slice()
                  .sort((a, b) => b.votes - a.votes)
                  .map((item) => (
                    <div key={item.id}>
                      <span>{item.nickname}</span>
                      <strong>{item.votes}</strong>
                    </div>
                  ))}
              </div>
              {isHost ? (
                <div className="result-actions">
                  <button className="primary-action" onClick={nextRound}>
                    Следующий раунд
                  </button>
                  <button onClick={backToLobby}>Вернуться в лобби</button>
                </div>
              ) : (
                <p className="muted">Ведущий выбирает, играть дальше или вернуться в лобби.</p>
              )}
            </div>
          )}

          <form className="chat-panel" onSubmit={sendMessage}>
            <div className="chat-log">
              {messages.length === 0 ? (
                <p className="muted">Чат комнаты пуст.</p>
              ) : (
                messages.map((item) => (
                  <p key={item.id}>
                    <strong>{item.nickname}:</strong> {item.message}
                  </p>
                ))
              )}
            </div>
            <div className="chat-row">
              <input
                value={message}
                maxLength={180}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Сообщение"
              />
              <button disabled={!message.trim()}>Отправить</button>
            </div>
          </form>
        </section>
      )}
    </main>
  );
}

export default App;
